import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button, Group, Modal, Progress, ScrollArea, Stack, Text } from "@mantine/core"
import { Markdown } from "../../Markdown"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/**
 * Brand-aware "Update available" modal for the desktop app. Pure presentation — the smart
 * wrapper (DesktopUpdateGate) owns the Tauri updater check, download progress and relaunch.
 *
 * While downloading the modal locks itself (no dismiss) so the user can't close it mid-swap.
 */
export const UpdateModalView = ({
  opened,
  version,
  notes,
  state,
  progress,
  errorMessage,
  onInstall,
  onDismiss,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const downloading = state === "downloading"

  return (
    <Modal
      opened={opened}
      onClose={onDismiss}
      centered
      withCloseButton={!downloading}
      closeOnClickOutside={!downloading}
      closeOnEscape={!downloading}
      title={<Trans>Update available</Trans>}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          <Trans>Version {version} is ready to install.</Trans>
        </Text>

        {notes ? (
          <ScrollArea.Autosize mah={260} type="auto">
            <Markdown content={notes} className={classes.notes} />
          </ScrollArea.Autosize>
        ) : null}

        {state === "error" && errorMessage ? (
          <Text size="sm" c="danger">
            {errorMessage}
          </Text>
        ) : null}

        {downloading ? (
          <Stack gap={4}>
            <Progress value={progress} striped animated aria-label={i18n._(msg`Update download progress`)} />
            <Text size="xs" c="dimmed">
              <Trans>Downloading update… {progress}%</Trans>
            </Text>
          </Stack>
        ) : null}

        <Group justify="flex-end">
          <Button variant="default" onClick={onDismiss} disabled={downloading}>
            <Trans>Later</Trans>
          </Button>
          <Button color="brand" onClick={onInstall} loading={downloading}>
            {state === "error" ? <Trans>Retry</Trans> : <Trans>Install & relaunch</Trans>}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
